import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CriticalCardDetail from './criticalCardDetail';
import { useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePage } from '../../context/PageContext';
import { useDeleteTask } from '../../hooks/useDeleteTask';
import { useFetchTaskDetail } from '../../hooks/useFetchTaskDetail';
import { ITask } from '../../interface/tasks';
import { TaskDetailSections, taskFields, taskRelations } from '../../constants/tasks';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import ConfirmModal from '../confirmationmodal/confirmationModal';
import { Delete, LinearCloseCirlce } from '../icons';
import { useTask } from '../../context/TaskContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import Notes from '../conflictSearch/notes';
import { NotesEntity } from '../../constants/notes';
import CustomTabs from '../customTabs/customTabs';
import { useUser } from '../../context/UserContext';
import CustomModal from '../informationPopup/customModal';
import { UserType } from '../../constants/constants';
import { useEmailCategoryTagger } from '../../hooks/useEmailCategoryTagger';
import { useMarkActivityAsCompleted } from '../../hooks/useMarkActivityAsCompleted';
import { useOpenActivity } from '../../hooks/useOpenActivity';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const CriticalTabView = () => {
  const { t } = useTranslation();
  const { updateTask } = useTask();
  const { id: taskId } = useParams();
  const { user } = useUser();

  const location = useLocation();
  const fromTab = Object.values(TaskDetailSections).includes(location?.state?.fromTab)
    ? location.state.fromTab
    : TaskDetailSections.general;
  const { goBack } = useNavigationStack();
  const { untagEmailCategory } = useEmailCategoryTagger();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [unableToDeleteTaskModal, setUnableToDeleteTaskModal] = useState<boolean>(false);
  const [markAsCompleteConfirmationModalVisible, setMarkAsCompleteConfirmationModalVisible] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<string>(String(fromTab));
  const { setTitle } = usePage();
  const deleteTaskMutation = useDeleteTask();
  const markAsCompletedMutation = useMarkActivityAsCompleted();
  const openActivityMutation = useOpenActivity();
  const {
    data: task,
    isLoading,
    refetch,
  } = useFetchTaskDetail({
    id: taskId,
    relations: [...taskRelations, 'otherResponsibleUsers.user:fullName'],
    fields: taskFields,
  });
  const markTaskAsCompleteOrOpen = (operation?: string) => {
    if (!task) return;
    const onSettled = {
      onSettled() {
        queryClient.removeQueries({ queryKey: ['task-details'] });
        refetch();
      },
    };
    if (operation === 'complete') {
      markAsCompletedMutation.mutate(
        {
          id: task?.id,
          version: task?.version,
        },
        onSettled,
      );
    } else {
      openActivityMutation.mutate(
        {
          id: task?.id,
          version: task?.version,
        },
        onSettled,
      );
    }
  };
  const handleMarkAsCompleteOrOpen = (task?: ITask, operation?: string) => {
    if (task) {
      if (operation === 'open') {
        markTaskAsCompleteOrOpen(operation);
        return;
      }
      const createTransactionWhenMarkAsDone = task.activityType.createTransactionWhenMarkedAsDone;
      const workTimeInvoicedThroughActivity = task.activityType.workTimeInvoicedThroughActivity;
      const workTimesDurationMoreThan0 = task.workTimes?.some((workTime) => workTime.duration > 0);
      if (
        !createTransactionWhenMarkAsDone &&
        workTimeInvoicedThroughActivity &&
        workTimesDurationMoreThan0 &&
        !task.transactionId
      ) {
        setMarkAsCompleteConfirmationModalVisible(true);
        return;
      }
      markTaskAsCompleteOrOpen('complete');
    }
  };
  const items = useMemo(
    () => [
      {
        key: String(TaskDetailSections.general),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <CriticalCardDetail
              task={task}
              completeOpenTask={handleMarkAsCompleteOrOpen}
              isLoading={isLoading}
            />
          </DetailScrollArea>
        ),
      },
      {
        key: String(TaskDetailSections.notes),
        label: t('common:tab.notes'),
        children: <Notes entitySubject={task?.subject ?? ''} entity={NotesEntity.ACTIVITY} />,
      },
    ],
    [task],
  );
  const handleConfirmDelete = () => {
    if (user?.userType !== UserType.ADMIN && task?.responsibleUserId !== user?.id) {
      setUnableToDeleteTaskModal(true);
    } else {
      setShowDeleteConfirm(true);
    }
  };
  const handleBack = () => {
    updateTask(null);
    goBack();
  };

  const deleteTask = () => {
    deleteTaskMutation.mutate(taskId ?? '', {
      async onSuccess() {
        const categoryName = `[CSI - ${t('task:criticalTask.criticalTask')}]`;
        await untagEmailCategory(categoryName, { criticalTaskId: '' });
        handleBack();
      },
      onSettled() {
        setShowDeleteConfirm(false);
      },
    });
  };

  useEffect(() => {
    if (task) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={task?.subject}
          subTitle={t('task:criticalTask.criticalTask')}
          handleDelete={handleConfirmDelete}
        />,
      );
      updateTask(task);
    }
  }, [task]);

  return (
    <div className="dashboard-wrapper">
      <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />

      <ConfirmModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        icon={<Delete />}
        title={t('task:deleteTaskQuestion')}
        subtitle={t('task:deleteTaskText')}
        cancelText={t('common:cancel')}
        confirmText={t('note:delete')}
        onConfirm={deleteTask}
      />
      <CustomModal
        visible={unableToDeleteTaskModal}
        icon={<LinearCloseCirlce />}
        title={t('task:noRightsToDeleteOtherUsersTask')}
        confirmText={t('common:ok')}
        onConfirm={() => {
          setUnableToDeleteTaskModal(false);
        }}
      />
      <CustomModal
        visible={markAsCompleteConfirmationModalVisible}
        title={t('task:markAsCompleteConfirmation')}
        cancelText={t('common:cancel')}
        confirmText={t('common:continue')}
        onConfirm={() => {
          markTaskAsCompleteOrOpen('complete');
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
        onClose={() => {
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
      />
    </div>
  );
};

export default CriticalTabView;
